import { useEffect, useMemo, useState } from "react";
import { X, ZoomIn, ZoomOut, Frame } from "lucide-react";
import { resolveImage } from "@/lib/api";

/**
 * Wall View — composite an artwork at real-world scale onto a photographed wall,
 * so a buyer can see what "11×14 above the couch" actually looks like.
 *
 * The reference photo has been chosen so the wall's visible horizontal span
 * roughly equals ROOM_WALL_INCHES inches. Artwork size in inches is scaled
 * against that real-world reference and rendered as % width of the room image.
 */
const ROOM_PHOTOS = [
  {
    key: "living",
    label: "Living Room",
    src: "https://images.unsplash.com/photo-1618220179428-22790b461013?auto=format&fit=crop&w=1600&q=80",
    wall_inches: 144, // wall visible in photo ≈ 12 ft
    anchor_top_pct: 30, // where the artwork center should sit vertically
  },
  {
    key: "bedroom",
    label: "Bedroom",
    src: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1600&q=80",
    wall_inches: 132,
    anchor_top_pct: 28,
  },
  {
    key: "office",
    label: "Office",
    src: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80",
    wall_inches: 120,
    anchor_top_pct: 34,
  },
];

const SIZES = [
  { key: "4x6", label: '4 × 6"', w: 4, h: 6 },
  { key: "8x10", label: '8 × 10"', w: 8, h: 10 },
  { key: "11x14", label: '11 × 14"', w: 11, h: 14 },
  { key: "16x20", label: '16 × 20"', w: 16, h: 20 },
  { key: "24x36", label: '24 × 36"', w: 24, h: 36 },
];

export default function WallView({ imageSrc, alt = "", onClose }) {
  const [roomKey, setRoomKey] = useState(ROOM_PHOTOS[0].key);
  const [sizeKey, setSizeKey] = useState("11x14");

  const room = useMemo(() => ROOM_PHOTOS.find(r => r.key === roomKey) || ROOM_PHOTOS[0], [roomKey]);
  const size = useMemo(() => SIZES.find(s => s.key === sizeKey) || SIZES[2], [sizeKey]);

  // Width of artwork as a percent of room photo width.
  const widthPct = (size.w / room.wall_inches) * 100;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[1000] bg-black/95 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="wall-view-modal"
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-4 right-4 w-10 h-10 bg-white/5 hover:bg-[#C9A055] hover:text-black border border-white/20 text-[#F4F1EA] flex items-center justify-center z-10"
        aria-label="Close"
        data-testid="wall-view-close"
      >
        <X size={16} />
      </button>

      <div className="w-full max-w-[1100px] my-8" onClick={(e) => e.stopPropagation()}>
        <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-2 text-center">Wall View · See it at home</div>
        <h2 className="font-display text-2xl md:text-3xl text-[#F4F1EA] mb-4 text-center tracking-wide">{size.label} on the wall</h2>

        {/* Room composite */}
        <div className="relative bg-[#0a0a0a] border border-white/10 overflow-hidden" data-testid="wall-view-room">
          <img
            src={room.src}
            alt={`${room.label} reference`}
            className="block w-full h-auto select-none bg-[#1a1a1a]"
            draggable={false}
            onError={(e) => {
              // Fallback: render a neutral wall block so the framed artwork still demos well.
              const fb = "data:image/svg+xml;utf8," + encodeURIComponent(
                `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 1067'>` +
                `<defs><linearGradient id='g' x1='0' y1='0' x2='0' y2='1'>` +
                `<stop offset='0' stop-color='#d8cfc1'/><stop offset='1' stop-color='#a89f8f'/>` +
                `</linearGradient></defs>` +
                `<rect width='1600' height='720' fill='url(#g)'/>` +
                `<rect y='720' width='1600' height='347' fill='#3b2f24'/>` +
                `<text x='800' y='1020' text-anchor='middle' font-family='serif' font-size='22' fill='#e7e0d3'>Reference wall preview</text>` +
                `</svg>`
              );
              if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
            }}
          />
          <div
            className="absolute left-1/2 -translate-x-1/2"
            style={{
              top: `${room.anchor_top_pct}%`,
              width: `${widthPct}%`,
              aspectRatio: `${size.w} / ${size.h}`,
              boxShadow: "0 14px 32px rgba(0,0,0,0.55), 0 0 0 8px #111 inset, 0 0 0 9px rgba(201,160,85,0.6) inset",
              backgroundColor: "#0a0a0a",
            }}
            data-testid="wall-view-frame"
          >
            <img
              src={resolveImage(imageSrc)}
              alt={alt}
              className="absolute inset-[8px] w-[calc(100%-16px)] h-[calc(100%-16px)] object-contain bg-[#050505]"
              draggable={false}
            />
          </div>
          {/* Subtle floor shadow for realism */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent" />
        </div>

        {/* Controls */}
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div>
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#8a857d] mb-2">Room</div>
            <div className="flex flex-wrap gap-2" data-testid="wall-view-rooms">
              {ROOM_PHOTOS.map(r => (
                <button
                  key={r.key}
                  onClick={() => setRoomKey(r.key)}
                  className={`px-3 py-1.5 font-ui text-[10px] tracking-[0.22em] uppercase border ${roomKey === r.key ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/15 text-[#F4F1EA] hover:border-[#C9A055]"}`}
                  data-testid={`wall-view-room-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#8a857d] mb-2">Print size</div>
            <div className="flex flex-wrap gap-2" data-testid="wall-view-sizes">
              {SIZES.map(s => (
                <button
                  key={s.key}
                  onClick={() => setSizeKey(s.key)}
                  className={`px-3 py-1.5 font-ui text-[10px] tracking-[0.22em] uppercase border ${sizeKey === s.key ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/15 text-[#F4F1EA] hover:border-[#C9A055]"}`}
                  data-testid={`wall-view-size-${s.key}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="mt-4 font-body italic text-[12px] text-[#8a857d] text-center">
          Wall view is a guide — actual room dimensions vary. Wall in this scene ≈ {Math.round(room.wall_inches / 12)} ft wide.
        </p>
      </div>
    </div>
  );
}

WallView.icon = Frame;
